import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * Used for BOTH /reset-password and /activate routes.
 * - mode="reset"    -> POST /auth/reset-password
 * - mode="activate" -> POST /auth/activate
 */
export default function SetPassword({ mode = "reset" }) {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const token = sp.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (pw !== pw2) { toast.error("Passwords don't match"); return; }
        if (!token) { toast.error("Missing token in link"); return; }
        setBusy(true);
        try {
            const url = mode === "activate" ? "/auth/activate" : "/auth/reset-password";
            await api.post(url, { token, new_password: pw });
            toast.success(mode === "activate" ? "Account activated" : "Password updated");
            await checkAuth();
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const title = mode === "activate" ? "Activate your account" : "Set a new password";
    const sub = mode === "activate"
        ? "Welcome! Pick a strong password to finish setting up your account."
        : "Pick a new password to regain access.";

    return (
        <div className="min-h-screen bg-white flex items-center justify-center p-8">
            <form onSubmit={submit} className="w-full max-w-md">
                <div className="tag-pill mb-6">{mode === "activate" ? "Activate" : "Reset"}</div>
                <h2 className="text-3xl font-bold tracking-tight mb-2">{title}</h2>
                <p className="text-sm text-zinc-500 mb-8">{sub}</p>
                {!token && (
                    <div className="mb-6 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                        Token missing — this link looks incomplete. Ask your administrator to resend.
                    </div>
                )}
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wider text-zinc-500">New Password</Label>
                        <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-none" data-testid="setpw-1" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Confirm</Label>
                        <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11 rounded-none" data-testid="setpw-2" />
                    </div>
                </div>
                <Button type="submit" disabled={busy || !token} className="w-full mt-6 rounded-none h-12 bg-[#002FA7] hover:bg-[#00227B]" data-testid="setpw-submit">
                    {busy ? "Working…" : (mode === "activate" ? "Activate & sign in" : "Update password")}
                </Button>
                <p className="mt-6 text-xs text-zinc-500 font-mono">
                    <Link to="/login" className="text-[#002FA7] hover:underline">← Back to sign in</Link>
                </p>
            </form>
        </div>
    );
}
